/**
 * Utility function to reliably open PDFs in a new tab across all devices
 * Works on mobile, tablet (including iPad), and desktop browsers
 * Special handling for iOS Safari which has strict security policies
 */

// Store a reference to a window opened during user gesture (for iOS Safari)
let pendingWindow = null;

/**
 * Enhanced detection for iOS devices (iPhone, iPad, or iPod)
 * Handles modern iPads (iPadOS 13+) which masquerade as desktop Safari
 */
function isIOSDevice() {
  const ua = navigator.userAgent;
  
  // Traditional iOS devices (iOS < 13)
  if (/iPad|iPhone|iPod/.test(ua)) {
    return true;
  }
  
  // Modern iPads (iPadOS 13+) report as MacIntel
  // Multiple detection methods for better reliability
  
  // Method 1: Check for touch support on Mac
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) {
    return true;
  }
  
  // Method 2: Check for iPad-specific features
  if (navigator.platform === 'MacIntel' && 'ontouchend' in document) {
    return true;
  }
  
  // Method 3: Check user agent for Mac + Safari with touch
  if (/Macintosh/.test(ua) && 'ontouchend' in document) {
    return true;
  }
  
  return false;
}

/**
 * Detects if the browser is Safari (not Chrome or other browsers)
 */
function isSafariBrowser() {
  const ua = navigator.userAgent;
  // Safari but not Chrome, Chromium-based, or Firefox
  return /Safari/.test(ua) && !/Chrome|CriOS|Chromium|FxiOS|EdgiOS/.test(ua);
}

/**
 * Call this immediately in the user gesture handler (before async operations)
 * to preserve the user gesture context for iOS Safari
 */
export function preparePdfWindow() {
  const isIOS = isIOSDevice();
  const isSafari = isSafariBrowser();
  const isIOSSafari = isIOS && isSafari;
  
  console.log('preparePdfWindow - isIOS:', isIOS, 'isSafari:', isSafari, 'isIOSSafari:', isIOSSafari);
  
  if (isIOSSafari) {
    try {
      // Open blank window immediately while we have user gesture context
      pendingWindow = window.open('about:blank', '_blank');
      if (pendingWindow) {
        // Write loading message to the pending window
        pendingWindow.document.write('<html><body><p>Loading PDF...</p></body></html>');
        return true;
      }
    } catch (error) {
      console.warn("Failed to prepare window for iOS Safari:", error);
    }
  }
  return false;
}

export function openPdfInNewTab(url) {
  if (!url) {
    console.error("No URL provided to openPdfInNewTab");
    return;
  }

  // Detect iOS Safari (including modern iPads)
  const isIOS = isIOSDevice();
  const isSafari = isSafariBrowser();
  const isIOSSafari = isIOS && isSafari;
  
  console.log('openPdfInNewTab - URL:', url, 'isIOS:', isIOS, 'isSafari:', isSafari);

  // CRITICAL: For iOS Safari, ONLY use the pending window or navigate current window
  if (isIOSSafari) {
    // Method 1: Use pending window if available (opened during user gesture)
    if (pendingWindow && !pendingWindow.closed) {
      try {
        console.log('Using pending window for iOS Safari');
        pendingWindow.location.href = url;
        pendingWindow = null; // Clear reference
        return;
      } catch (error) {
        console.warn("Failed to navigate pending window:", error);
        pendingWindow = null;
      }
    }
    
    // Method 2: Try window.open one more time (might work if in gesture context)
    try {
      console.log('Trying window.open for iOS Safari');
      const newWindow = window.open(url, '_blank');
      if (newWindow && !newWindow.closed) {
        console.log('window.open succeeded for iOS Safari');
        return;
      }
    } catch (error) {
      console.warn("iOS Safari window.open failed:", error);
    }
    
    // Method 3: Create invisible iframe (alternative approach for iOS Safari)
    try {
      console.log('Trying iframe approach for iOS Safari');
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = url;
      document.body.appendChild(iframe);
      
      // Clean up after PDF loads
      setTimeout(() => {
        if (iframe.parentNode) {
          document.body.removeChild(iframe);
        }
      }, 1000);
      return;
    } catch (error) {
      console.warn("iOS Safari iframe method failed:", error);
    }
    
    // Method 4: Final fallback - navigate in current window
    console.log('Final fallback: navigating current window');
    window.location.href = url;
    return;
  }

  // For non-iOS-Safari browsers, use standard methods
  // Method 1: Try using an anchor element (most reliable)
  try {
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    // Clean up after a short delay
    setTimeout(() => {
      if (link.parentNode) {
        document.body.removeChild(link);
      }
    }, 100);
    return;
  } catch (error) {
    console.warn("Anchor method failed, trying window.open:", error);
  }

  // Method 2: Fallback to window.open with proper parameters
  try {
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
    // Check if the popup was blocked
    if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
      // If blocked, fall back to navigating in current window
      window.location.href = url;
    }
  } catch (error) {
    console.warn("window.open failed, using location.href:", error);
    // Final fallback: navigate in current window
    window.location.href = url;
  }
}
